import { Check, CheckCircle2 } from "lucide-react";

import {
  formatSponsorLabel,
  poll2AllFilled,
  poll2FilledCount,
  poll2Lookup,
  poll2RunningTotal,
  type Poll2Dimension,
  type Poll2Draft,
  type Poll2Tier,
} from "./poll2Helpers";
import {
  mciButtonClass,
  statusBadgeClass,
} from "../../Utils/Helper/sponsorEngagementUi";

type Props = {
  accountId: string;
  accountName: string;
  sponsorName?: string;
  sponsorRole?: "primary" | "secondary" | string | null;
  dimensions: Poll2Dimension[];
  poll2Tiers: Poll2Tier[];
  draft: Poll2Draft;
  submitted: boolean;
  submitting: boolean;
  savingDraft?: boolean;
  onDraftChange: (draft: Poll2Draft) => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  showHeader?: boolean;
};

const Poll2FeedbackForm = ({
  accountId,
  accountName,
  sponsorName,
  sponsorRole,
  dimensions,
  poll2Tiers,
  draft,
  submitted,
  submitting,
  savingDraft = false,
  onDraftChange,
  onSaveDraft,
  onSubmit,
  showHeader = true,
}: Props) => {
  const readOnly = submitted;
  const filled = poll2FilledCount(draft);
  const allFilled = poll2AllFilled(draft);
  const total = poll2RunningTotal(draft);
  const tierObj = total > 0 ? poll2Lookup(total, poll2Tiers) : null;
  const sponsorLabel = formatSponsorLabel(sponsorName, sponsorRole);

  const setDim = (dimId: string, value: number) => {
    if (readOnly) return;
    if (dimId === "involvement") {
      onDraftChange({ ...draft, involvement: value });
    } else if (dimId === "relationship") {
      onDraftChange({ ...draft, relationship: value });
    } else if (dimId === "knowledge") {
      onDraftChange({ ...draft, knowledge: value });
    }
  };

  const setEvidence = (dimId: string, value: string) => {
    if (readOnly) return;
    onDraftChange({
      ...draft,
      evidence: { ...draft.evidence, [dimId]: value },
    });
  };

  return (
    <div>
      <div className="py-4 px-5 pb-5">
        {showHeader ? (
          <div className="flex items-start justify-between gap-3 mb-4 pb-3 border-b border-gray-100">
            <div>
              <h3 className="m-0 text-base font-semibold text-gray-800">
                {accountName}
              </h3>
              {sponsorLabel ? (
                <p className="m-0 mt-1 text-xs text-gray-500">{sponsorLabel}</p>
              ) : null}
            </div>
            <span
              className={statusBadgeClass(
                readOnly ? "completed" : filled > 0 ? "draft" : "pending"
              )}
            >
              {readOnly
                ? "Submitted"
                : filled > 0
                  ? `Draft · ${filled}/3`
                  : "Pending"}
            </span>
          </div>
        ) : null}

        {readOnly ? (
          <div className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3 mb-4">
            <CheckCircle2 className="shrink-0 text-green-600 mt-px" size={18} />
            <div className="min-w-0">
              <div className="font-bold text-[13px] mb-px text-green-700">
                Submitted
              </div>
              <div className="text-xs text-gray-500">
                Your Poll 2 feedback is read-only. Score {total} / 15
                {tierObj?.label ? ` · ${tierObj.label}` : ""}.
              </div>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          {dimensions.map(d => {
            const dimVal =
              d.id === "involvement"
                ? draft.involvement
                : d.id === "relationship"
                  ? draft.relationship
                  : draft.knowledge;
            const evVal = draft.evidence[d.id] ?? "";
            return (
              <div
                key={d.id}
                className="p-3.5 px-4 bg-gray-50 border border-gray-200 rounded-lg"
              >
                <div className="text-[13px] font-bold text-gray-800 mb-0.5">
                  {d.title}
                </div>
                <div className="text-[11.5px] text-gray-500 mb-2">{d.desc}</div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      className={`flex-1 py-1.5 px-1 rounded text-[13px] font-semibold text-center border transition-all disabled:cursor-default ${
                        dimVal === n
                          ? "bg-purple-600 border-purple-600 text-white"
                          : "bg-white border-gray-300 text-gray-700 hover:border-purple-400 hover:text-purple-600 disabled:opacity-45"
                      }`}
                      disabled={readOnly}
                      onClick={() => setDim(d.id, n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                  <span>{d.lo}</span>
                  <span>{d.hi}</span>
                </div>
                {d.evidence_type === "text" ? (
                  <div className="mt-2 flex flex-col gap-1">
                    <label
                      htmlFor={`p2-ev-${accountId}-${d.id}`}
                      className="text-[11px] font-semibold text-gray-600 flex items-center gap-1.5"
                    >
                      <Check width={11} height={11} aria-hidden />
                      {d.evidence_label}
                    </label>
                    <textarea
                      id={`p2-ev-${accountId}-${d.id}`}
                      className="w-full min-h-[48px] py-1.5 px-2.5 border border-gray-300 rounded text-[12.5px] resize-y focus:outline-none focus:border-purple-600 focus:shadow-[0_0_0_2px_#faf5ff] disabled:bg-gray-50 disabled:text-gray-700 disabled:cursor-default"
                      placeholder={d.evidence_placeholder ?? ""}
                      value={evVal}
                      readOnly={readOnly}
                      disabled={readOnly}
                      onChange={e => setEvidence(d.id, e.target.value)}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}

          <div className="py-3 px-3.5 rounded-md flex items-center justify-between text-[13px] bg-gray-50 border border-gray-200">
            <span className="font-semibold text-gray-700">Running Total</span>
            <span className="text-base font-extrabold text-gray-800">
              {allFilled || (readOnly && total > 0)
                ? `${total} / 15${tierObj?.label ? ` · ${tierObj.label}` : ""}`
                : `${filled}/3 dims · ${total} pts`}
            </span>
          </div>

          <div className="mt-3 p-3.5 px-4 bg-gray-50 border border-gray-200 rounded-lg">
            <label
              htmlFor={`p2-notes-${accountId}`}
              className="block text-xs font-semibold text-gray-700 mb-1.5"
            >
              Comments (optional)
            </label>
            <textarea
              id={`p2-notes-${accountId}`}
              className="w-full min-h-[80px] py-2 px-2.5 border border-gray-200 rounded-md text-[13px] resize-y bg-white focus:outline-none focus:border-purple-600 focus:shadow-[0_0_0_2px_#faf5ff] disabled:bg-gray-50 disabled:text-gray-700 disabled:cursor-default"
              placeholder="Anything qualitative the ESL should know (recent incident, schedule conflicts, strategic shift)."
              value={draft.comments}
              readOnly={readOnly}
              disabled={readOnly}
              onChange={e =>
                onDraftChange({ ...draft, comments: e.target.value })
              }
            />
          </div>

          {!readOnly ? (
            <div className="flex gap-2 justify-end mt-4 pt-3.5 border-t border-gray-200">
              <button
                type="button"
                className={mciButtonClass("secondary")}
                onClick={onSaveDraft}
                disabled={submitting || savingDraft}
              >
                {savingDraft ? "Saving…" : "Save Draft"}
              </button>
              <button
                type="button"
                className={mciButtonClass("primary")}
                disabled={!allFilled || submitting}
                onClick={onSubmit}
              >
                {submitting ? "Submitting…" : "Submit Feedback"}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Poll2FeedbackForm;
